import React, { useState, useEffect } from "react";
import { Form, Input, Button, Card, message, Result, Spin } from "antd";
import { LockOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../const";

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #014c75 0%, #00d9a9 100%);
  padding: 20px;
`;

const InviteCard = styled(Card)`
  width: 100%;
  max-width: 480px;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  
  .ant-card-body {
    padding: 40px;
  }
  
  .logo {
    text-align: center;
    margin-bottom: 24px;
    
    h1 {
      color: #014c75;
      font-size: 32px;
      font-weight: 700;
      margin: 0;
    }
  }
  
  .invite-info {
    text-align: center;
    margin-bottom: 32px;
    
    h2 {
      color: #333;
      font-size: 20px;
      margin-bottom: 12px;
    }
    
    p {
      color: #666;
      font-size: 14px;
      margin: 0;
    }
    
    .org-name {
      color: #00d9a9;
      font-weight: 600;
    }
    
    .role-name {
      color: #014c75;
      font-weight: 500;
    }
  }
`;

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get("token");

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setError("Invalid invitation link");
      setLoading(false);
    }
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API_CONST.VALIDATE_INVITATION}/${token}`);
      setInvitation(response.data.invitation);
    } catch (err) {
      setError(err.response?.data?.error || "Invalid or expired invitation");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (values.password !== values.confirmPassword) {
      message.error("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(API_CONST.ACCEPT_INVITATION, {
        token,
        password: values.password,
      });
      setSuccess(true);
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to accept invitation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoToLogin = () => {
    navigate("/");
  };

  if (loading) {
    return (
      <PageWrapper>
        <InviteCard>
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16, color: "#666" }}>Validating invitation...</p>
          </div>
        </InviteCard>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <InviteCard>
          <Result
            status="error"
            icon={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
            title="Invalid Invitation"
            subTitle={error}
            extra={
              <Button type="primary" onClick={handleGoToLogin} style={{ background: "#014c75" }}>
                Go to Login
              </Button>
            }
          />
        </InviteCard>
      </PageWrapper>
    );
  }

  if (success) {
    return (
      <PageWrapper>
        <InviteCard>
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: "#00d9a9" }} />}
            title="Welcome to the Team!"
            subTitle="Your account has been created successfully. You can now log in with your email and password."
            extra={
              <Button
                type="primary"
                size="large"
                onClick={handleGoToLogin}
                style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
              >
                Go to Login
              </Button>
            }
          />
        </InviteCard>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <InviteCard>
        <div className="logo">
          <h1>HireNXT</h1>
        </div>

        <div className="invite-info">
          <h2>Welcome, {invitation?.name}!</h2>
          <p>
            You've been invited to join <span className="org-name">{invitation?.organizationName}</span>
            {invitation?.roleName && (
              <>
                {" "}as a <span className="role-name">{invitation?.roleName}</span>
              </>
            )}
          </p>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          size="large"
        >
          <Form.Item label="Email">
            <Input value={invitation?.email} disabled style={{ background: "#f5f5f5" }} />
          </Form.Item>

          <Form.Item
            label="Create Password"
            name="password"
            rules={[
              { required: true, message: "Please enter a password" },
              { min: 6, message: "Password must be at least 6 characters" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter your password"
            />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm your password"
            />
          </Form.Item>

          <Form.Item style={{ marginTop: 24 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              block
              style={{
                height: 48,
                background: "#00d9a9",
                borderColor: "#00d9a9",
                fontSize: 16,
              }}
            >
              Accept Invitation & Create Account
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <p style={{ color: "#666", fontSize: 13 }}>
            Already have an account?{" "}
            <a href="/" style={{ color: "#014c75" }}>
              Login here
            </a>
          </p>
        </div>
      </InviteCard>
    </PageWrapper>
  );
};

export default AcceptInvitation;
